import React from 'react';
import { Grid } from '@mui/material';
import InputConfig from '../../../../../../Components/InputConfig';
import { PRESET_FLOORS_SERIES, SERIES_EXPERIMENT_MAP } from '../../../../../../core/constants';

import { useState } from 'react';
import {
	Box,
	Card,
	CardActionArea,
	CardContent,
	Typography,
	Chip,
	Paper,
	Collapse,
	Stack,
	Button,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { globalFloorsMapping } from '../../../../../../core/constants/gamUprConstants';
import DropDownMultiple from '../../../../../../Components/DropDownMultiple';

const FloorSeriesModalSelector = ({ editable, floorSeries, onApply }) => {
	const [open, setOpen] = useState(false);

	const [tempSeries, setTempSeries] = useState('preset1');
	const [tempCustomFloors, setTempCustomFloors] = useState([]);

	const getActiveFloors = (series, customList) => {
		return series === 'custom' ? customList : PRESET_FLOORS_SERIES[series];
	};

	const detectSeriesType = (floors) => {
		if (!floors || floors.length === 0) return 'preset1';
		if (JSON.stringify(floors) === JSON.stringify(PRESET_FLOORS_SERIES.preset1)) return 'preset1';
		if (JSON.stringify(floors) === JSON.stringify(PRESET_FLOORS_SERIES.preset2)) return 'preset2';
		return 'custom';
	};

	const handleOpen = () => {
		const seriesType = detectSeriesType(floorSeries);
		setTempSeries(seriesType);
		// keep as strings to match validGamUprFloorValues options
		setTempCustomFloors(seriesType === 'custom' ? floorSeries || [] : []);
		setOpen(true);
	};

	const handleClose = () => setOpen(false);

	const handleSave = () => {
		const floors = getActiveFloors(tempSeries, tempCustomFloors);
		onApply({
			floors: floors.map(String),
			experimentNumber: SERIES_EXPERIMENT_MAP[tempSeries]
		});
		setOpen(false);
	};

	const handleCustomFloorsChange = (newValue) => {
		const sortedValues = [...newValue].sort((a, b) => parseFloat(b) - parseFloat(a));
		setTempCustomFloors(sortedValues);
	};

	const handleCopyFloors = () => {
		const text = JSON.stringify(tempCustomFloors);
		navigator.clipboard.writeText(text).catch(() => alert('Failed to copy to clipboard.'));
	};

	const handlePasteFloors = async () => {
		try {
			const text = await navigator.clipboard.readText();
			const parsed = JSON.parse(text);
			if (!Array.isArray(parsed)) throw new Error('Pasted value is not an array.');
			const validSet = new Set(Object.keys(globalFloorsMapping).slice(4));
			const valid = parsed.map(String).filter((v) => validSet.has(v));
			const invalid = parsed.map(String).filter((v) => !validSet.has(v));
			if (invalid.length > 0) {
				alert(`Skipped invalid floor values: ${invalid.join(', ')}`);
			}
			const sorted = valid.sort((a, b) => parseFloat(b) - parseFloat(a));
			setTempCustomFloors(sorted);
		} catch (err) {
			alert(`Paste failed: ${err.message}`);
		}
	};

	const currentFloorsPreview = floorSeries || [];

	return (
		<Box sx={{ pt: 1.7, maxWidth: 600 }}>
			{/* TRIGGER BUTTON WITH PREVIEW */}
			<Typography variant="h6" color="text.secondary" sx={{ fontSize: '1.4rem', mb: 0.5 }}>
				Floor Series
			</Typography>
			<Paper
				variant="default"
				sx={{
					p: 1,
					display: 'flex',
					flexDirection: { xs: 'column', sm: 'row' },
					alignItems: 'center',
					justifyContent: 'space-between',
					gap: 2,
					bgcolor: 'background.paper',
					borderRadius: 2,
					border: '1px solid #c3c0c0'
				}}
			>
				<Box sx={{ textAlign: 'left' }}>
					<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
						{currentFloorsPreview.length > 0 ? (
							<>
								<Chip label={currentFloorsPreview[0]} size="small" variant="outlined" /> --
								<Chip
									label={currentFloorsPreview[currentFloorsPreview.length - 1]}
									size="small"
									variant="outlined"
								/>
							</>
						) : (
							<Typography variant="body2" color="error" sx={{ fontStyle: 'italic' }}>
								No floors configured
							</Typography>
						)}
					</Stack>
				</Box>

				<IconButton
					variant="contained"
					onClick={handleOpen}
					aria-label="Edit Floor Series"
					color="primary"
					disableElevation
					disabled={!editable}
				>
					<EditIcon fontSize="inherit" />
				</IconButton>
			</Paper>

			<Dialog
				open={open}
				onClose={handleClose}
				fullWidth
				maxWidth="md"
				aria-labelledby="floor-series-dialog-title"
			>
				<DialogTitle id="floor-series-dialog-title" sx={{ fontWeight: 600 }}>
					Configure Floor Series
				</DialogTitle>

				<DialogContent dividers>
					{/* 1. Presets Row inside Modal */}
					<Grid container spacing={2} sx={{ mb: 3, mt: 0.5 }}>
						{/* Standard */}
						<Grid item xs={12} sm={4}>
							<Card
								variant="outlined"
								sx={{
									borderColor: tempSeries === 'preset1' ? 'primary.main' : 'divider',
									borderWidth: tempSeries === 'preset1' ? 2 : 1,
									bgcolor: tempSeries === 'preset1' ? 'action.hover' : 'background.paper'
								}}
							>
								<CardActionArea onClick={() => setTempSeries('preset1')} sx={{ p: 1 }}>
									<CardContent sx={{ position: 'relative' }}>
										{tempSeries === 'preset1' && (
											<CheckCircleOutlineIcon
												color="primary"
												sx={{ position: 'absolute', top: 12, right: 12, fontSize: 18 }}
											/>
										)}
										<Typography variant="subtitle2" fontWeight="bold">
											Preset Floors 1
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											display="block"
											sx={{ mt: 0.5 }}
										>
											{PRESET_FLOORS_SERIES.preset1[0]} --{' '}
											{PRESET_FLOORS_SERIES.preset1[PRESET_FLOORS_SERIES.preset1.length - 1]}
										</Typography>
									</CardContent>
								</CardActionArea>
							</Card>
						</Grid>

						{/* Preset 2 */}
						<Grid item xs={12} sm={4}>
							<Card
								variant="outlined"
								sx={{
									borderColor: tempSeries === 'preset2' ? 'primary.main' : 'divider',
									borderWidth: tempSeries === 'preset2' ? 2 : 1,
									bgcolor: tempSeries === 'preset2' ? 'action.hover' : 'background.paper'
								}}
							>
								<CardActionArea onClick={() => setTempSeries('preset2')} sx={{ p: 1 }}>
									<CardContent sx={{ position: 'relative' }}>
										{tempSeries === 'preset2' && (
											<CheckCircleOutlineIcon
												color="primary"
												sx={{ position: 'absolute', top: 12, right: 12, fontSize: 18 }}
											/>
										)}
										<Typography variant="subtitle2" fontWeight="bold">
											Preset Floors 2
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											display="block"
											sx={{ mt: 0.5 }}
										>
											{PRESET_FLOORS_SERIES.preset2[0]} --{' '}
											{PRESET_FLOORS_SERIES.preset2[PRESET_FLOORS_SERIES.preset2.length - 1]}
										</Typography>
									</CardContent>
								</CardActionArea>
							</Card>
						</Grid>

						{/* Custom */}
						<Grid item xs={12} sm={4}>
							<Card
								variant="outlined"
								sx={{
									borderColor: tempSeries === 'custom' ? 'primary.main' : 'divider',
									borderWidth: tempSeries === 'custom' ? 2 : 1,
									bgcolor: tempSeries === 'custom' ? 'action.hover' : 'background.paper'
								}}
							>
								<CardActionArea onClick={() => setTempSeries('custom')} sx={{ p: 1 }}>
									<CardContent sx={{ position: 'relative' }}>
										<SettingsIcon
											color={tempSeries === 'custom' ? 'primary' : 'disabled'}
											sx={{ position: 'absolute', top: 12, right: 12, fontSize: 18 }}
										/>
										<Typography variant="subtitle2" fontWeight="bold">
											Custom
										</Typography>
										<Typography
											variant="caption"
											color="text.secondary"
											display="block"
											sx={{ mt: 0.5 }}
										>
											Build your own custom sequence
										</Typography>
									</CardContent>
								</CardActionArea>
							</Card>
						</Grid>
					</Grid>

					{/* 2. Conditional Custom Dropdown inside Modal */}
					<Collapse in={tempSeries === 'custom'} timeout="auto">
						<Box
							sx={{
								p: 2.5,
								border: '1px dashed',
								borderColor: 'divider',
								borderRadius: 2,
								bgcolor: 'grey.50',
								mb: 2
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									mb: 1
								}}
							>
								<Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
									CHOOSE FLOORS FROM GLOBAL POOL
								</Typography>
								<Box style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
									<span>
										<IconButton
											size="small"
											onClick={handleCopyFloors}
											disabled={tempCustomFloors.length === 0}
											aria-label="Copy floors"
											style={{ gap: 3 }}
										>
											<ContentCopyIcon fontSize="small" /> Copy
										</IconButton>
									</span>

									<IconButton
										size="small"
										onClick={handlePasteFloors}
										aria-label="Paste floors"
										style={{ gap: 3 }}
									>
										<ContentPasteIcon fontSize="small" /> Paste
									</IconButton>
								</Box>
							</Box>
							<DropDownMultiple
								isObjectEditor={false}
								directValue={tempCustomFloors}
								updateDirectValue={handleCustomFloorsChange}
								list={Object.keys(globalFloorsMapping).slice(4)}
								label="Global Floors"
								formControlStyles={{ width: '100%' }}
							/>
						</Box>
					</Collapse>

					{/* 3. Live Draft Preview (Inside Modal) */}
					<Box sx={{ mt: 2, p: 1.5, bgcolor: 'action.selected', borderRadius: 1 }}>
						<Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
							Draft Sequence Preview:
						</Typography>
						<Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
							{getActiveFloors(tempSeries, tempCustomFloors).length > 0 ? (
								getActiveFloors(tempSeries, tempCustomFloors).map((floor, idx) => (
									<Chip key={idx} label={floor} size="small" variant="filled" color="secondary" />
								))
							) : (
								<Typography variant="caption" color="error" sx={{ fontStyle: 'italic' }}>
									Please select at least one floor.
								</Typography>
							)}
						</Stack>
					</Box>
				</DialogContent>

				<DialogActions sx={{ px: 3, py: 2 }}>
					<Button onClick={handleClose} color="inherit">
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						disableElevation
						disabled={tempSeries === 'custom' && tempCustomFloors.length === 0}
					>
						Apply Changes
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

const FOWModel = ({ engineModelConfig, index, handleConfigsChange, keyData, enabled }) => {
	return (
		<>
			<Grid item xs={6} sm={6}>
				<InputConfig
					keyData={`${keyData}.configs.${index}.fillThreshold`}
					label="Fill Threshold"
					inputType="number"
					isObjectEditor={false}
					labelStyles={{ fontSize: '1.5rem' }}
					containerStyles={{ marginTop: '1rem', marginLeft: '0.4rem' }}
					directValue={engineModelConfig?.fillThreshold}
					updateDirectValue={(val) => handleConfigsChange(index, 'fillThreshold', val)}
					editable={enabled && engineModelConfig.incDecEnabled}
					showInfoButton={true}
					infoMessage="The number of fills after which the cycle resets & starts from the top floor"
				/>
			</Grid>

			<Grid item xs={12} sm={6}>
				<InputConfig
					keyData={`${keyData}.configs.${index}.sliceFloorsSteps`}
					label="Slice Floors Steps"
					inputType="number"
					isObjectEditor={false}
					labelStyles={{ fontSize: '1.5rem' }}
					containerStyles={{ marginTop: '1rem', marginLeft: '0.4rem' }}
					directValue={engineModelConfig?.sliceFloorsSteps || 0}
					updateDirectValue={(val) => handleConfigsChange(index, 'sliceFloorsSteps', val)}
					editable={enabled}
					showInfoButton={true}
					infoMessage="The number of steps by which the floors gets cut down after each cycle in case there's unfilled"
				/>
			</Grid>

			<Grid item xs={6}>
				<FloorSeriesModalSelector
					editable={enabled}
					floorSeries={engineModelConfig?.floorSeries || []}
					onApply={({ floors, experimentNumber }) => {
						handleConfigsChange(index, { floorSeries: floors, experimentNumber });
					}}
				/>
			</Grid>
		</>
	);
};

export default FOWModel;
